<template>
  <div class="knowledge-page">
    <AppHeader />

    <KnowledgeBanner />

    <div class="knowledge-page__body">
      <div class="knowledge-page__inner">
        <KnowledgeSidebar
          :active-type.sync="sidebarType"
          :types="ontologyTypes"
        />

        <div class="knowledge-page__content">
          <SearchPanel
            ref="searchPanelRef"
            :view-mode.sync="viewMode"
            :total="total"
            :active-tags="activeTags"
            :type-options="ontologyTypes"
            :attribute-options="attributeOptions"
            :default-type="sidebarType"
            @search="handleSearch"
            @reset="handleReset"
            @remove-tag="removeTag"
            @clear-tags="clearTags"
          />

          <ResultList
            v-loading="loading"
            :list="list"
            :view-mode="viewMode"
          />

          <div v-if="total" class="knowledge-page__pagination">
            <el-pagination
              :current-page.sync="currentPage"
              :page-size="pageSize"
              :total="total"
              layout="prev, pager, next"
              background
              @current-change="fetchList"
            />
          </div>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import AppFooter from '@/components/common/AppFooter.vue'
import KnowledgeBanner from './components/KnowledgeBanner.vue'
import KnowledgeSidebar from './components/KnowledgeSidebar.vue'
import SearchPanel from './components/SearchPanel.vue'
import ResultList from './components/ResultList.vue'
import {
  searchKnowledgeEntities,
  fetchKnowledgeFacets,
  mapKnowledgeItem,
  mapIdentityFacets,
  mapAttributeOptions
} from '@/api/knowledge.js'
import { ontologyTypes as defaultOntologyTypes } from './data.js'

export default {
  name: 'KnowledgePage',
  components: {
    AppHeader,
    AppFooter,
    KnowledgeBanner,
    KnowledgeSidebar,
    SearchPanel,
    ResultList
  },
  data() {
    return {
      ontologyTypes: defaultOntologyTypes,
      attributeOptions: [],
      sidebarType: '',
      activeTags: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      list: [],
      loading: false,
      viewMode: 'list',
      searchParams: {
        type: '',
        attribute: '',
        keyword: ''
      }
    }
  },
  watch: {
    sidebarType() {
      this.currentPage = 1
      this.fetchData()
    },
    '$route.query.keyword'(value) {
      this.applyKeywordFromRoute(value)
    }
  },
  mounted() {
    document.documentElement.classList.add('hide-scrollbar')
    this.applyKeywordFromRoute(this.$route.query.keyword, true)
  },
  activated() {
    document.documentElement.classList.add('hide-scrollbar')
  },
  deactivated() {
    document.documentElement.classList.remove('hide-scrollbar')
  },
  beforeDestroy() {
    document.documentElement.classList.remove('hide-scrollbar')
  },
  methods: {
    applyKeywordFromRoute(keyword, fetchOnEmpty = false) {
      const normalized = (keyword || '').trim()
      if (normalized) {
        this.$nextTick(() => {
          if (this.$refs.searchPanelRef) {
            this.$refs.searchPanelRef.setKeyword(normalized)
          }
        })
        this.handleSearch({ type: '', attribute: '', keyword: normalized })
        return
      }
      if (fetchOnEmpty) {
        this.fetchData()
      }
    },
    resolveIdentityId() {
      const key = this.sidebarType || this.searchParams.type
      if (!key) {
        return undefined
      }
      const matched = this.ontologyTypes.find((item) => item.key === key)
      return matched && matched.id ? matched.id : undefined
    },
    buildQueryParams() {
      const identityId = this.resolveIdentityId()
      return {
        page: this.currentPage,
        limit: this.pageSize,
        identityId,
        attributeKey: this.searchParams.attribute || undefined,
        keyword: this.searchParams.keyword || undefined
      }
    },
    buildFacetParams() {
      const params = this.buildQueryParams()
      return {
        identityId: params.identityId,
        attributeKey: params.attributeKey,
        keyword: params.keyword
      }
    },
    async fetchData() {
      this.loading = true
      try {
        const listParams = this.buildQueryParams()
        const facetParams = this.buildFacetParams()
        const [pageRes, facetsRes] = await Promise.all([
          searchKnowledgeEntities(listParams),
          fetchKnowledgeFacets(facetParams)
        ])
        this.list = (pageRes.records || []).map(mapKnowledgeItem)
        this.total = pageRes.total || 0
        this.ontologyTypes = mapIdentityFacets(facetsRes.identities, defaultOntologyTypes)
        this.attributeOptions = mapAttributeOptions(facetsRes.attributes)
      } catch (error) {
        this.list = []
        this.total = 0
      } finally {
        this.loading = false
      }
    },
    fetchList() {
      this.fetchData()
    },
    handleSearch(params) {
      this.searchParams = { ...params }
      this.currentPage = 1
      if (params.keyword && !this.activeTags.includes(params.keyword)) {
        this.activeTags = [...this.activeTags, params.keyword]
      }
      this.fetchData()
    },
    handleReset() {
      this.sidebarType = ''
      this.searchParams = { type: '', attribute: '', keyword: '' }
      this.currentPage = 1
      this.fetchData()
    },
    removeTag(tag) {
      this.activeTags = this.activeTags.filter((item) => item !== tag)
      if (this.searchParams.keyword === tag) {
        this.searchParams.keyword = ''
      }
      this.currentPage = 1
      this.fetchData()
    },
    clearTags() {
      this.activeTags = []
      this.searchParams.keyword = ''
      this.currentPage = 1
      this.fetchData()
    }
  }
}
</script>

<style lang="scss" scoped>
.knowledge-page {
  min-height: 100vh;
  background:
    url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.035'/%3E%3C/svg%3E"),
    $color-paper;

  &__body {
    padding: $spacing-xl $content-padding $spacing-4xl;
  }

  &__inner {
    display: flex;
    gap: $spacing-xl;
    max-width: $container-max-width;
    margin: 0 auto;
    align-items: flex-start;
  }

  &__content {
    flex: 1;
    min-width: 0;
  }

  &__pagination {
    display: flex;
    justify-content: center;
    margin-top: $spacing-xl;
    padding-top: $spacing-base;
  }
}

@media (max-width: 960px) {
  .knowledge-page {
    &__inner {
      flex-direction: column;
    }
  }
}
</style>
